import {
  BorderTransportType,
  CustomsCalculationInput,
  CustomsCalculationResult,
  CustomsCalculationResultSchema,
} from '../schemas';

export type { BorderTransportType };

/**
 * SUBAGENTE 4: ARCA / AFIP CUSTOMS & FRANCHISE ENGINE
 * 
 * Calculates Argentine customs duty (Régimen de Equipaje - Ley 22.415 / Resoluciones ARCA):
 * - Terrestrial Border (Puente de la Amistad / Tancredo Neves): $300 USD / adult.
 * - Airport Border (Cataratas / Foz do Iguaçu): $500 USD / adult.
 * - Minors under 16 receive 50% of the adult franchise.
 * - Family group pooling allowed for married couples and minors.
 * - Taxable excess is subject to a flat 50% duty rate.
 * - Converted to ARS at official Banco Nación Argentina (BNA) seller rate without surcharge taxes.
 */

export const FRANCHISE_LIMITS = {
  terrestrial_river: {
    adult: 300,
    minor: 150,
  },
  air_maritime: {
    adult: 500,
    minor: 250,
  },
} as const;

export function calculateArcaCustomsDuty(
  input: CustomsCalculationInput,
  officialRateBna = 1080.0
): CustomsCalculationResult {
  const limits = FRANCHISE_LIMITS[input.transportType];
  const adultFranchise = limits.adult;
  const minorFranchise = limits.minor;

  const totalAdultsFranchise = input.adultsCount * adultFranchise;
  const totalMinorsFranchise = input.minorsUnder16Count * minorFranchise;
  const totalFranchiseUSD = totalAdultsFranchise + totalMinorsFranchise;

  const taxableExcessUSD = Math.max(0, input.itemsTotalUSD - totalFranchiseUSD);
  const dutyToPayUSD = Number((taxableExcessUSD * 0.5).toFixed(2));
  const dutyToPayARS = Number((dutyToPayUSD * officialRateBna).toFixed(2));
  const isExempt = taxableExcessUSD === 0;

  const notes: string[] = [];

  if (isExempt) {
    notes.push(
      `Tus compras ($${input.itemsTotalUSD.toFixed(2)} USD) están 100% dentro de la franquicia total del grupo familiar ($${totalFranchiseUSD.toFixed(2)} USD). No abonas arancel aduanero.`
    );
  } else {
    notes.push(
      `Superaste la franquicia grupal por $${taxableExcessUSD.toFixed(2)} USD. El arancel a pagar en Aduana ARCA es del 50% sobre el excedente ($${dutyToPayUSD.toFixed(2)} USD).`
    );
    notes.push(
      `El pago en el puesto de control fronterizo se liquida en Pesos Argentinos ($${dutyToPayARS.toLocaleString('es-AR')} ARS) a la cotización oficial BNA ($${officialRateBna.toFixed(2)}).`
    );
  }

  notes.push(
    'Nota: Un teléfono celular y una notebook o tablet por pasajero están exentos como efectos personales de uso propio (RG 4331).'
  );

  const result: CustomsCalculationResult = {
    transportType: input.transportType,
    individualAdultFranchiseUSD: adultFranchise,
    individualMinorFranchiseUSD: minorFranchise,
    totalFranchiseUSD,
    itemsTotalUSD: input.itemsTotalUSD,
    taxableExcessUSD,
    dutyRatePercentage: 50,
    dutyToPayUSD,
    dutyToPayARS,
    officialRateBNA: officialRateBna,
    isExempt,
    notes,
  };

  return CustomsCalculationResultSchema.parse(result);
}
